import { computed, inject, Injectable, Signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { BreakpointObserver } from "@angular/cdk/layout";
import { map } from "rxjs";
import { DeviceDetectorService } from "ngx-device-detector";

/**
 * Media queries mirroring `theme.screens` in `tailwind.config.js` (`sm`, `lg`, `xl`), so JS-driven
 * layout flips on the same pixel as the `sm:`/`lg:`/`xl:` CSS variants. Keep each in sync with
 * its Tailwind counterpart; `breakpoints.spec.ts` fails if they drift.
 */
export const SM_BREAKPOINT = "(max-width: 639.98px)";
export const LG_BREAKPOINT = "(max-width: 1024px)";
export const XL_BREAKPOINT = "(max-width: 1279.98px)";
/** At/below this the side nav is the rail and expanding it opens an overlay drawer. */
export const RAIL_BREAKPOINT = "(max-width: 1100px)";
/** At/below this there is no room for the rail; a hamburger in the page header opens the drawer. */
export const HAMBURGER_BREAKPOINT = "(max-width: 640px)";
/** Must match the `max-width: 768px` bottom-sheet block in material-ui.scss. */
export const BOTTOM_SHEET_BREAKPOINT = "(max-width: 768px)";

/** Signal that tracks whether a media query currently matches. Must be called from an
 *  injection context (e.g. a component field initializer or constructor). */
export function observeBreakpoint(query: string): Signal<boolean> {
  const breakpointObserver = inject(BreakpointObserver);
  return toSignal(breakpointObserver.observe(query).pipe(map((s) => s.matches)), {
    initialValue: false,
  });
}

/**
 * True when the viewport is at/below `breakpoint` (default `SM_BREAKPOINT`) OR the device is
 * UA-detected as mobile. `details-wrapper`'s own mobile-layout switch (and any consumer driving
 * it via `[forceSmallScreen]`/`[isOverlay]`) ORs in `DeviceDetectorService.isMobile()`
 * unconditionally, so a page mirroring that switch must fold the same signal into its own
 * breakpoint check — otherwise a UA-detected mobile device at a wider viewport (e.g. a phone in
 * landscape) ends up with the parent page's overlay/layout state disagreeing with
 * details-wrapper's own mobile header.
 */
export function observeSmallScreen(breakpoint: string = SM_BREAKPOINT): Signal<boolean> {
  const isNarrowViewport = observeBreakpoint(breakpoint);
  const deviceService = inject(DeviceDetectorService);
  return computed(() => isNarrowViewport() || deviceService.isMobile());
}

/** The app's viewport breakpoints as signals, so components inject one thing and read a signal
 *  instead of wiring `BreakpointObserver`/`toSignal`/`map` themselves. Viewport width only; it does
 *  not fold in UA-detected mobile (see `observeSmallScreen` for that). */
@Injectable({ providedIn: "root" })
export class BreakpointService {
  readonly isSmOrSmaller = observeBreakpoint(SM_BREAKPOINT);
  readonly isLgOrSmaller = observeBreakpoint(LG_BREAKPOINT);
  readonly isXlOrSmaller = observeBreakpoint(XL_BREAKPOINT);
}
